// The /alerts List-archetype view (UI-LAW §4c, alerts.md): one PageHeader
// with tab-contextual create actions, Tabs (Rules · Channels · History · Gate runs,
// default History), and one full-height keyset-paginated DataTable per tab.
// Props-only — the container owns queries, mutations, and search navigation.

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import type { ListQueryResult } from "../../lib/useListQuery";
import type {
  AlertsCreateKind,
  AlertsTab,
  AlertsTypeFilter,
  AlertsWhenFilter,
} from "../../lib/routerSearch";
import {
  Badge,
  Button,
  Code,
  EmptyState,
  HelpTip,
  Inline,
  InlineError,
  Page,
  PageHeader,
  TabPanel,
  Tabs,
  Text,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { docUrl } from "../../lib/docLinks";
import { resultTone } from "../GateRunDetail";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import {
  AlertHistoryFilters,
  type AlertHistoryFilterValues,
} from "./AlertHistoryFilters";
import { configSummary, deliverySummary, ruleSummary } from "./alertSummaries";

type GateRun = components["schemas"]["GateRunResponse"];

type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertRule = components["schemas"]["AlertRuleResponse"];
type AlertEvent = components["schemas"]["AlertEventResponse"];

const alertsTabs: Array<{ id: AlertsTab; label: string }> = [
  { id: "rules", label: "Rules" },
  { id: "channels", label: "Channels" },
  { id: "history", label: "History" },
  { id: "gate-runs", label: "Gate runs" },
];

// Row-click opens the detail drawer, so the per-row Actions column is gone
// and the columns are pure data (delete lives in the drawer, §2.12).
// UI-LAW §4 DataTable / §4b.4 facet uniformity: sortable-value columns carry
// accessors so click-header sort works. Trigger and state are the
// low-cardinality facets; event triggers have no threshold state.
const ruleColumns: Array<ColumnDef<AlertRule>> = [
  {
    header: "Name",
    accessorKey: "display_name",
    cell: ({ row }) => (
      <NameWithId
        id={row.original.id}
        idLabel="Rule ID"
        name={row.original.display_name}
        strong
      />
    ),
  },
  {
    header: "Trigger",
    id: "trigger",
    accessorFn: (rule) => rule.trigger.kind,
  },
  {
    header: "Severity",
    accessorKey: "severity",
    cell: ({ row }) => <Badge tone="neutral">{row.original.severity}</Badge>,
  },
  {
    header: "Condition",
    cell: ({ row }) => ruleSummary(row.original),
  },
  {
    header: "State",
    id: "state",
    accessorFn: (rule) =>
      rule.trigger.kind === "threshold" ? rule.trigger.state : "event",
    cell: ({ row }) => (
      <Badge
        tone={
          row.original.trigger.kind === "threshold" &&
          row.original.trigger.state === "firing"
            ? "danger"
            : row.original.trigger.kind === "threshold"
              ? "success"
              : "neutral"
        }
      >
        {row.original.trigger.kind === "threshold"
          ? row.original.trigger.state
          : "event"}
      </Badge>
    ),
  },
  {
    header: "Last value",
    cell: ({ row }) =>
      row.original.trigger.kind !== "threshold" ||
      row.original.trigger.last_value == null
        ? "none"
        : row.original.trigger.last_value.toFixed(4),
  },
];

const channelColumns: Array<ColumnDef<AlertChannel>> = [
  {
    header: "Name",
    accessorKey: "display_name",
    cell: ({ row }) => (
      <NameWithId
        id={row.original.id}
        idLabel="Channel ID"
        name={row.original.display_name}
        strong
      />
    ),
  },
  { header: "Type", accessorKey: "channel_type" },
  {
    header: "Target",
    cell: ({ row }) => configSummary(row.original),
  },
  {
    header: "Updated",
    accessorKey: "updated_at",
    cell: ({ row }) => <DateCell value={row.original.updated_at} />,
  },
];

// History is the analysis-flavored list: every column carries an accessor so
// the UI-LAW §4c List CSV export serializes the full row; id-shaped columns
// opt out of sorting (not sortable values, work-order 14b).
function eventColumns(
  ruleById: ReadonlyMap<string, AlertRule>,
): Array<ColumnDef<AlertEvent>> {
  return [
    {
      header: "State",
      id: "state",
      accessorFn: (event) => event.event_type,
      cell: ({ row }) => (
        <Badge tone="neutral">{row.original.event_type}</Badge>
      ),
    },
    {
      header: "Type",
      id: "type",
      accessorFn: (event) =>
        ruleById.get(event.alert_rule_id)?.trigger.kind ?? "unavailable",
    },
    {
      header: "Severity",
      id: "severity",
      accessorFn: (event) =>
        ruleById.get(event.alert_rule_id)?.severity ?? "unavailable",
    },
    {
      header: "Rule",
      id: "rule",
      accessorFn: (event) =>
        ruleById.get(event.alert_rule_id)?.display_name ?? "Deleted alert rule",
      cell: ({ row }) => (
        <Text>
          {ruleById.get(row.original.alert_rule_id)?.display_name ??
            "Deleted alert rule"}
        </Text>
      ),
    },
    {
      header: "Event",
      accessorKey: "event_key",
      enableSorting: false,
      cell: ({ row }) => <Code>{row.original.event_key}</Code>,
    },
    {
      header: "Delivery",
      id: "delivery",
      accessorFn: (event) => deliverySummary(event),
      enableSorting: false,
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
  ];
}

function targetSummary(run: GateRun): string {
  const target = run.target;
  if (target.mode === "batch" && typeof target.batch_id === "string") {
    return `batch ${target.batch_id}`;
  }
  if (target.mode === "metric" && typeof target.window_seconds === "number") {
    return `${String(target.window_seconds)}s window`;
  }
  return typeof target.mode === "string" ? target.mode : "metric";
}

function conditionSummary(run: GateRun): string {
  return `${run.measure} ${run.comparator} ${String(run.threshold)}`;
}

// UI-LAW §4 DataTable / §4b.4 facet / §4c List uniformity: gate runs are an
// analysis-flavored list, so every column carries an accessor for the
// loaded-rows CSV export; `verdict`/`status` are the low-cardinality facets
// (scope comes from the root team/agent selector);
// summary columns opt out of sorting (not sortable values).
function gateColumns(): Array<ColumnDef<GateRun>> {
  return [
    {
      header: "Verdict",
      accessorKey: "verdict",
      cell: ({ row }) => (
        <Badge tone={resultTone(row.original)}>{row.original.verdict}</Badge>
      ),
    },
    { header: "Status", accessorKey: "status" },
    {
      header: "Condition",
      id: "condition",
      accessorFn: (run) => conditionSummary(run),
      enableSorting: false,
    },
    {
      header: "Value",
      accessorKey: "value",
      cell: ({ row }) => row.original.value.toFixed(4),
    },
    {
      header: "Occurrences",
      accessorKey: "occurrences",
      cell: ({ row }) => String(row.original.occurrences),
    },
    {
      header: "Target",
      id: "target",
      accessorFn: (run) => targetSummary(run),
      enableSorting: false,
    },
    {
      header: "Evaluated",
      accessorKey: "evaluated_at",
      cell: ({ row }) => <DateCell value={row.original.evaluated_at} />,
    },
  ];
}

export interface AlertsViewProps {
  activeTab: AlertsTab;
  authorityErrorMessage?: string;
  canAuthor: boolean;
  canAuthorChannels: boolean;
  channels: ListQueryResult<AlertChannel>;
  eventCreatedAfter?: string;
  eventCreatedBefore?: string;
  eventType?: AlertsTypeFilter;
  eventWhen?: AlertsWhenFilter;
  events: ListQueryResult<AlertEvent>;
  gateRuns: ListQueryResult<GateRun>;
  onOpenRun: (id: string) => void;
  onOpenChannel: (id: string) => void;
  onOpenEvent: (id: string) => void;
  onOpenRule: (id: string) => void;
  onEventFiltersChange: (filters: AlertHistoryFilterValues) => void;
  onRetryAuthority: () => void;
  onStartCreate: (kind: AlertsCreateKind) => void;
  onStartCreateRule: () => void;
  onTabChange: (tab: AlertsTab) => void;
  rules: ListQueryResult<AlertRule>;
}

export function AlertsView({
  activeTab,
  authorityErrorMessage,
  canAuthor,
  canAuthorChannels,
  channels,
  eventCreatedAfter,
  eventCreatedBefore,
  eventType,
  eventWhen,
  events,
  gateRuns,
  onOpenRun,
  onEventFiltersChange,
  onOpenChannel,
  onOpenEvent,
  onOpenRule,
  onRetryAuthority,
  onStartCreate,
  onStartCreateRule,
  onTabChange,
  rules,
}: AlertsViewProps) {
  const ruleById = new Map(rules.items.map((rule) => [rule.id, rule]));
  const filteredEvents =
    eventType == null
      ? events.items
      : events.items.filter((event) =>
          eventType === "event"
            ? ["batch_completed", "scheduled_run_failed"].includes(
                event.event_type,
              )
            : !["batch_completed", "scheduled_run_failed"].includes(
                event.event_type,
              ),
        );

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          canAuthor && activeTab === "rules" ? (
            <Button size="sm" onClick={onStartCreateRule} type="button">
              Create rule
            </Button>
          ) : canAuthorChannels && activeTab === "channels" ? (
            <Button
              size="sm"
              onClick={() => {
                onStartCreate("channel");
              }}
              type="button"
            >
              Create channel
            </Button>
          ) : undefined
        }
        tabs={
          <Tabs
            activeId={activeTab}
            ariaLabel="Alerts"
            onSelect={(id) => {
              onTabChange(id as AlertsTab);
            }}
            tabs={alertsTabs}
          />
        }
      />

      {authorityErrorMessage != null ? (
        <Inline gap={3}>
          <InlineError>{authorityErrorMessage}</InlineError>
          <Button onClick={onRetryAuthority} type="button" variant="secondary">
            Retry permissions
          </Button>
        </Inline>
      ) : null}

      <TabPanel activeId={activeTab} fill tabId="rules">
        <PaginatedTableSection
          ariaLabel="Alert rules"
          columns={ruleColumns}
          emptyState={
            <EmptyState
              action={
                canAuthor ? (
                  <Button onClick={onStartCreateRule} type="button">
                    Create rule
                  </Button>
                ) : undefined
              }
              title="No alert rules"
            />
          }
          enableSorting
          errorMessage={rules.errorMessage}
          filterableColumns={["trigger", "state"]}
          getRowId={(rule) => rule.id}
          hasNextPage={rules.hasNextPage}
          isFetchingNextPage={rules.isFetchingNextPage}
          isPending={rules.isPending}
          items={rules.items}
          listStateKey={rules.listStateKey}
          onLoadMore={rules.fetchNextPage}
          onRowClick={(row) => {
            onOpenRule(row.original.id);
          }}
          skeletonLabel="Loading alert rules"
          skeletonRows={3}
          virtual={{ fill: true, interactiveRows: true }}
        />
      </TabPanel>

      <TabPanel activeId={activeTab} fill tabId="channels">
        <PaginatedTableSection
          ariaLabel="Alert channels"
          columns={channelColumns}
          emptyState={
            <EmptyState
              action={
                canAuthorChannels ? (
                  <Button
                    onClick={() => {
                      onStartCreate("channel");
                    }}
                    type="button"
                  >
                    Create channel
                  </Button>
                ) : undefined
              }
              title="No alert channels"
            />
          }
          enableSorting
          errorMessage={channels.errorMessage}
          filterableColumns={["channel_type"]}
          getRowId={(channel) => channel.id}
          hasNextPage={channels.hasNextPage}
          isFetchingNextPage={channels.isFetchingNextPage}
          isPending={channels.isPending}
          items={channels.items}
          listStateKey={channels.listStateKey}
          onLoadMore={channels.fetchNextPage}
          onRowClick={(row) => {
            onOpenChannel(row.original.id);
          }}
          skeletonLabel="Loading alert channels"
          skeletonRows={3}
          virtual={{ fill: true, interactiveRows: true }}
        />
      </TabPanel>

      <TabPanel activeId={activeTab} fill tabId="history">
        <AlertHistoryFilters
          onChange={onEventFiltersChange}
          value={{
            created_after: eventCreatedAfter,
            created_before: eventCreatedBefore,
            type: eventType,
            when: eventWhen,
          }}
        />
        <PaginatedTableSection
          ariaLabel="Alert events"
          columns={eventColumns(ruleById)}
          csvExport={{ fileName: "alert-events.csv" }}
          emptyState={
            <EmptyState
              action={
                events.hasNextPage ? (
                  <Button
                    disabled={events.isFetchingNextPage}
                    onClick={events.fetchNextPage}
                    type="button"
                    variant="secondary"
                  >
                    {events.isFetchingNextPage
                      ? "Loading..."
                      : "Load more history"}
                  </Button>
                ) : undefined
              }
              title="No alert events"
            />
          }
          enableSorting
          errorMessage={events.errorMessage}
          getRowId={(event) => event.id}
          hasNextPage={events.hasNextPage}
          isFetchingNextPage={events.isFetchingNextPage}
          isPending={events.isPending}
          items={filteredEvents}
          listStateKey={events.listStateKey}
          onLoadMore={events.fetchNextPage}
          onRowClick={(row) => {
            onOpenEvent(row.original.id);
          }}
          skeletonLabel="Loading alert history"
          skeletonRows={3}
          virtual={{ fill: true, interactiveRows: true }}
        />
      </TabPanel>
      <TabPanel activeId={activeTab} fill tabId="gate-runs">
        <PaginatedTableSection
          ariaLabel="Gate runs"
          columns={gateColumns()}
          csvExport={{ fileName: "gate-runs.csv" }}
          emptyState={
            <EmptyState
              description={
                <>
                  CI pipelines record gate runs by calling{" "}
                  <Code>gates:run</Code> via <Code>marker gate run</Code> or the
                  GitHub Action.
                </>
              }
              help={
                <HelpTip
                  docHref={`${docUrl("guides/track-results")}#gates`}
                  docLabel="Gate runs in CI"
                  label="About gate runs"
                >
                  Each run records a one-off CI verdict.
                </HelpTip>
              }
              title="No gate runs"
            />
          }
          enableSorting
          errorMessage={gateRuns.errorMessage}
          filterableColumns={["verdict", "status"]}
          getRowId={(run) => run.gate_run_id}
          hasNextPage={gateRuns.hasNextPage}
          isFetchingNextPage={gateRuns.isFetchingNextPage}
          isPending={gateRuns.isPending}
          items={gateRuns.items}
          listStateKey={gateRuns.listStateKey}
          onLoadMore={gateRuns.fetchNextPage}
          onRowClick={(row) => {
            onOpenRun(row.original.gate_run_id);
          }}
          skeletonLabel="Loading gate runs"
          skeletonRows={3}
          virtual={{ fill: true, interactiveRows: true }}
        />
      </TabPanel>
    </Page>
  );
}
